<template>
  <div class="step-panel">
    <!-- Step Header -->
    <div class="step-header">
      <h2>{{ t('Reembolsos de Despesas') }}</h2>
    </div>

    <!-- Helper Banner -->
    <div class="helper-banner">
      <i class="pi pi-info-circle"></i>
      <div class="helper-content">
        <strong>{{ t('Solicitações de Reembolso') }}</strong>
        <ul>
          <li>{{ t('Reembolsos são solicitações de colaboradores para reembolso de despesas pagas') }}</li>
          <li>{{ t('Você pode aprovar ou recusar cada solicitação individualmente') }}</li>
          <li>{{ t('Reembolsos aprovados criam despesas separadas (não descontam do salário)') }}</li>
        </ul>
      </div>
    </div>

    <!-- Warning Banner -->
    <div class="warning-banner">
      <i class="pi pi-exclamation-triangle"></i>
      <div class="warning-content">
        <strong>{{ t('Atenção') }}</strong>
        <p>{{ t('Reembolsos aprovados serão pagos como despesas separadas. Reembolsos recusados não serão pagos. Revise cuidadosamente antes de aprovar.') }}</p>
      </div>
    </div>

    <!-- Important Notes -->
    <div class="important-notes">
      <div class="notes-header">
        <i class="pi pi-info-circle"></i>
        <h3>{{ t('Próximos Passos') }}</h3>
      </div>
      <ul class="notes-list">
        <li>
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Após processar os reembolsos, prossiga para as deduções') }}</span>
        </li>
      </ul>
    </div>

    <p class="step-description">
      {{ t('Gerencie as solicitacoes de reembolso de despesas pagas pelos operadores para este periodo') }}
    </p>

    <inline-message severity="info" :closable="false" class="info-message">
      <i class="pi pi-info-circle"></i>
      {{ t('Solicitações de reembolso aceitas criarão despesas separadas (não são descontadas do salário). Você pode aceitar ou recusar cada solicitação individualmente.') }}
    </inline-message>

    <inline-message v-if="parseInt(String(periodStatus)) === 4" severity="warn" :closable="false" class="reconfirm-warning-message">
      <div class="warning-content">
        <i class="pi pi-exclamation-triangle" style="font-size: 1.25rem"></i>
        <h4 class="warning-title">{{ t('Período Já Confirmado') }}</h4>
        <p class="warning-text">
          {{ t('Este período já foi confirmado. Você ainda pode processar novas solicitações de reembolso pendentes.') }}
        </p>
      </div>
    </inline-message>

    <!-- Loading State from Machine -->
    <div v-if="isLoading" class="loading-container">
      <spinner data-testid="payroll-refunds-loading" />
    </div>

    <!-- Error State from Machine -->
    <div v-else-if="hasError" class="error-container">
      <inline-message severity="error" class="w-full">
        {{ errorMessage || t('Erro ao carregar reembolsos. Clique em tentar novamente.') }}
      </inline-message>
      <Button
        :label="t('Tentar Novamente')"
        icon="pi pi-refresh"
        @click="handleRetry"
        class="mt-3"
        data-testid="payroll-refunds-retry-button"
      />
    </div>

    <!-- Interactive State -->
    <div v-else class="refunds-container">
      <!-- Status Card -->
      <div class="status-card">
        <div class="card-body">
          <!-- Summary Grid -->
          <div class="summary-grid">
            <div class="summary-item">
              <div class="item-label">{{ t('Status') }}</div>
              <div class="item-value">
                <span class="status-indicator" :class="statusClass"></span>
                {{ t(statusText) }}
              </div>
            </div>

            <div class="summary-item">
              <div class="item-label">{{ t('Pendentes') }}</div>
              <div class="item-value pending-count">{{ pendingRefunds.length }}</div>
            </div>

            <div class="summary-item">
              <div class="item-label">{{ t('Aceitos') }}</div>
              <div class="item-value accepted-count">{{ acceptedCount }}</div>
            </div>

            <div class="summary-item highlight">
              <div class="item-label">{{ t('Total Pendente') }}</div>
              <div class="item-value">{{ n(totalPendingAmount, 'currency') }}</div>
            </div>
          </div>

          <!-- Info Message based on status -->
          <div v-if="pendingRefunds.length === 0 && acceptedCount === 0" class="info-message-inline pending">
            <i class="pi pi-info-circle"></i>
            <span>{{ t('Nenhuma solicitacao de reembolso encontrada para este periodo.') }}</span>
          </div>

          <div v-else-if="pendingRefunds.length === 0 && acceptedCount > 0" class="info-message-inline completed">
            <i class="pi pi-check-circle"></i>
            <span>{{ t('Todas as solicitacoes de reembolso foram processadas.') }}</span>
          </div>

          <div v-else class="info-message-inline in-progress">
            <i class="pi pi-clock"></i>
            <span>{{ t('Existem solicitacoes de reembolso pendentes aguardando processamento.') }}</span>
          </div>
        </div>
      </div>

      <!-- Pending Refunds List -->
      <div v-if="pendingRefunds.length > 0" class="pending-refunds-section">
        <h4>{{ t('Solicitacoes Pendentes') }}</h4>

        <div class="refunds-list">
          <div
            v-for="refund in pendingRefunds"
            :key="refund.id"
            class="refund-item"
          >
            <div class="refund-info">
              <div class="refund-header">
                <span class="refund-title">{{ refund.title }}</span>
                <PayrollRefundsStatusBadge :status="refund.status" />
              </div>

              <div class="refund-details">
                <div class="refund-amount">
                  <i class="pi pi-dollar"></i>
                  <span>{{ n(parseFloat(String(refund.amount ?? 0)) || 0, 'currency') }}</span>
                </div>
                <div class="refund-date">
                  <i class="pi pi-calendar"></i>
                  <span>{{ formatDate(refund.expense_date) }}</span>
                </div>
                <div v-if="getUserName(refund.user_id)" class="refund-user">
                  <i class="pi pi-user"></i>
                  <span>{{ getUserName(refund.user_id) }}</span>
                </div>
              </div>

              <div v-if="refund.description" class="refund-description">
                {{ refund.description }}
              </div>

              <div v-if="refund.file_url" class="refund-receipt">
                <a :href="refund.file_url" target="_blank" class="receipt-link">
                  <i class="pi pi-paperclip"></i>
                  {{ t('Ver comprovante') }}
                </a>
              </div>
            </div>

            <div class="refund-actions">
              <Button
                icon="pi pi-check"
                class="p-button-sm p-button-success"
                :title="t('Aceitar')"
                @click="handleAccept(refund)"
                :data-testid="`payroll-refunds-accept-${refund.id}`"
              />
              <Button
                icon="pi pi-times"
                class="p-button-sm p-button-danger"
                :title="t('Recusar')"
                @click="handleDecline(refund)"
                :data-testid="`payroll-refunds-decline-${refund.id}`"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Panel (if there are accepted refunds) -->
      <PayrollRefundsSummary
        v-if="acceptedCount > 0"
        :summary="summaryByUser"
        :total="totalAccepted"
        :users-map="usersMap"
      />
    </div>

    <!-- Accept Wizard Modal -->
    <PayrollRefundsAcceptWizard
      v-if="selectedRefund"
      :visible="showAcceptWizard"
      :refund="(selectedRefund as unknown as Record<string, unknown>)"
      @update:visible="showAcceptWizard = $event"
      @completed="onAcceptCompleted"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, watch, type Ref, type ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateFormatter } from '@/composables/useDateFormatter';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import { permissionsService } from '@/services/permissionsService';
import { usePayrollRefunds } from '@/composables/usePayrollRefunds';
import PayrollRefundsStatusBadge from '@/domains/payroll/refunds/PayrollRefundsStatusBadge.vue';
import PayrollRefundsAcceptWizard from '@/domains/payroll/refunds/PayrollRefundsAcceptWizard.vue';
import PayrollRefundsSummary from './PayrollRefundsSummary.vue';

type Id = number | string;

interface RefundItem {
  id: Id;
  title?: string;
  status?: number | string;
  amount?: number | string;
  expense_date?: string | Date | null;
  user_id?: Id;
  description?: string;
  file_url?: string;
  [key: string]: unknown;
}

interface RefundDecision {
  decision?: string;
  [key: string]: unknown;
}

interface RefundsApiData {
  available?: RefundItem[];
  decisions?: RefundDecision[];
  total_accepted?: number;
  summary_by_user?: Array<Record<string, unknown>>;
}

interface PaymentByUser {
  user_id?: Id;
  user_name?: string;
  [key: string]: unknown;
}

interface WizardMachineContext {
  period?: { id?: Id; franchise_id?: Id; status?: number | string } | null;
  periodFormData?: { franchise_id?: Id } | null;
  paymentByUser?: PaymentByUser[];
  data?: { refunds?: RefundsApiData };
  error?: { message?: string };
  [key: string]: unknown;
}

interface WizardMachineState {
  matches: (state: string) => boolean;
}

interface WizardMachine {
  context: Ref<WizardMachineContext> | ComputedRef<WizardMachineContext>;
  state: Ref<WizardMachineState> | ComputedRef<WizardMachineState>;
  send: (event: { type: string; [key: string]: unknown }) => void;
}

interface AcceptCompletedPayload {
  [key: string]: unknown;
}

// =============================================================================
// SINGLE SOURCE OF TRUTH: XState Machine
// =============================================================================

const machine = inject<WizardMachine>('wizardMachine') as WizardMachine;
const { t, n } = useI18n();
const { formatDate: formatDateLocale, fromApiDateToDate } = useDateFormatter();

// Keep only the declineRefund function from usePayrollRefunds
// The accept flow uses the wizard which handles its own API calls
const { declineRefund } = usePayrollRefunds();

// =============================================================================
// MACHINE STATE READ-ONLY ACCESS
// =============================================================================

/**
 * Period ID from machine context
 */
const periodId = computed<number | null>(() => {
  const id = machine.context.value.period?.id;
  return id ? Number(id) : null;
});

/**
 * Franchise ID from machine context
 */
const franchiseId = computed<number | null>(() => {
  return Number(machine.context.value.period?.franchise_id) ||
         Number(machine.context.value.periodFormData?.franchise_id) ||
         null;
});

/**
 * Period status from machine context
 */
const periodStatus = computed<number | string>(() => {
  return machine.context.value.period?.status || 0;
});

/**
 * Loading state from machine state
 */
const isLoading = computed<boolean>(() => {
  return machine.state.value.matches('refunds.loading');
});

/**
 * Error state from machine context
 */
const hasError = computed<boolean>(() => {
  return machine.state.value.matches('refunds.error');
});

/**
 * Error message from machine context
 */
const errorMessage = computed<string | null>(() => {
  return machine.context.value.error?.message || null;
});

/**
 * Refunds data from machine context
 */
const refundsData = computed(() => {
  const data = machine.context.value.data?.refunds;
  return {
    pending: data?.available || [],
    decisions: data?.decisions || [],
    totalAccepted: data?.total_accepted || 0,
    summaryByUser: data?.summary_by_user || []
  };
});

/**
 * Build users map from payment by user data
 */
const usersMap = computed<Record<string, { name: string }>>(() => {
  const users = machine.context.value.paymentByUser || [];
  const map: Record<string, { name: string }> = {};
  users.forEach(u => {
    if (u.user_id !== undefined && u.user_id !== null) {
      map[String(u.user_id)] = { name: u.user_name || `User ${u.user_id}` };
    }
  });
  return map;
});

// =============================================================================
// COMPUTED PROPERTIES (derived from machine state only)
// =============================================================================

/**
 * Pending refunds from machine context
 */
const pendingRefunds = computed<RefundItem[]>(() => {
  return refundsData.value.pending as RefundItem[];
});

/**
 * Summary by user from machine context
 */
const summaryByUser = computed(() => {
  return refundsData.value.summaryByUser;
});

/**
 * Total accepted refunds from machine context
 */
const totalAccepted = computed<number>(() => {
  return refundsData.value.totalAccepted;
});

/**
 * Accepted refund count
 */
const acceptedCount = computed<number>(() => {
  const decisions = refundsData.value.decisions || [];
  return decisions.filter(d => d.decision === 'accepted').length;
});

/**
 * Total pending amount
 */
const totalPendingAmount = computed<number>(() => {
  return pendingRefunds.value.reduce((sum, r) => sum + (parseFloat(String(r.amount ?? 0)) || 0), 0);
});

/**
 * Status class for indicator
 */
const statusClass = computed<string>(() => {
  if (pendingRefunds.value.length === 0 && acceptedCount.value === 0) {
    return 'empty';
  }
  if (pendingRefunds.value.length > 0) {
    return 'pending';
  }
  return 'completed';
});

/**
 * Status text
 */
const statusText = computed<string>(() => {
  if (pendingRefunds.value.length === 0 && acceptedCount.value === 0) {
    return 'Sem Solicitacoes';
  }
  if (pendingRefunds.value.length > 0) {
    return 'Pendente';
  }
  return 'Concluido';
});

// =============================================================================
// UI STATE (component-scoped only)
// =============================================================================

/**
 * Wizard visibility - UI state only, not data
 */
const showAcceptWizard = ref<boolean>(false);

/**
 * Selected refund for wizard - UI state only
 */
const selectedRefund = ref<RefundItem | null>(null);

// =============================================================================
// EVENT HANDLERS (send events to machine)
// =============================================================================

/**
 * Format date for display
 */
const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  if (date instanceof Date) {
    return formatDateLocale(date);
  }
  // Use fromApiDateToDate for date strings to avoid timezone issues
  // (midnight UTC displays as previous day in GMT-3)
  return formatDateLocale(fromApiDateToDate(date));
};

/**
 * Get user name from users map
 */
const getUserName = (userId: Id | null | undefined): string | null => {
  if (userId === null || userId === undefined) return null;
  const user = usersMap.value?.[String(userId)] as { name?: string; nome?: string } | undefined;
  return user?.name ||
         user?.nome ||
         permissionsService.getUserNameBySub(String(userId)) ||
         null;
};

/**
 * Open accept wizard
 */
const handleAccept = (refund: RefundItem): void => {
  log('PAYROLL_REFACTOR', 'REFACTOR_TRACE', 'StepPayrollRefunds', 'OPEN_WIZARD', {
    refundId: refund.id
  });
  selectedRefund.value = refund;
  showAcceptWizard.value = true;
};

/**
 * Handle decline refund
 * Send event to machine to process decline
 */
const handleDecline = async (refund: RefundItem): Promise<void> => {
  log('PAYROLL_REFACTOR', 'REFACTOR_TRACE', 'StepPayrollRefunds', 'DECLINE_REFUND', {
    refundId: refund.id
  });

  const reason = window.prompt(t('Motivo da recusa (opcional):'));

  // User cancelled
  if (reason === null) return;

  try {
    const result = await (declineRefund as (refundId: Id, reason?: string | null) => Promise<unknown>)(refund.id, reason || null);
    if (result) {
      eventBus.emit('globalMessage.success', {
        message: t('Solicitacao recusada com sucesso'),
        duration: 3000
      });

      log('PAYROLL_REFACTOR', 'DATA_FLOW', 'COMPONENT', 'WRITE', {
        via: 'machine.send()',
        event: 'RELOAD_REFUNDS',
        reason: 'Decline processed, triggering reload'
      });

      // Send event to machine to reload refunds data
      machine.send({ type: 'RELOAD_REFUNDS' });

      // Notify via eventBus
      eventBus.emit('payroll.refunds.updated');
    } else {
      eventBus.emit('globalMessage.error', {
        message: t('Erro ao recusar solicitacao'),
        duration: 5000
      });
    }
  } catch (err) {
    log('PAYROLL_REFACTOR', 'ERROR_TRACE', 'handleDecline', {
      message: (err as Error)?.message,
      refundId: refund.id
    });
    eventBus.emit('globalMessage.error', {
      message: (err as Error)?.message || t('Erro ao recusar solicitacao'),
      duration: 5000
    });
  }
};

/**
 * Handle accept wizard completed
 */
const onAcceptCompleted = (result: unknown): void => {
  log('PAYROLL_REFACTOR', 'REFACTOR_TRACE', 'StepPayrollRefunds', 'WIZARD_COMPLETED', result);

  showAcceptWizard.value = false;
  selectedRefund.value = null;

  // Send event to machine to reload refunds data
  log('PAYROLL_REFACTOR', 'DATA_FLOW', 'COMPONENT', 'WRITE', {
    via: 'machine.send()',
    event: 'RELOAD_REFUNDS',
    reason: 'Accept completed, triggering reload'
  });

  machine.send({ type: 'RELOAD_REFUNDS' });

  // Notify via eventBus
  eventBus.emit('payroll.refunds.updated');
};

/**
 * Handle retry on error
 */
const handleRetry = (): void => {
  log('PAYROLL_REFACTOR', 'REFACTOR_TRACE', 'StepPayrollRefunds', 'RETRY_LOAD');
  machine.send({ type: 'RETRY_REFUNDS' });
};

// =============================================================================
// LIFECYCLE & WATCH
// =============================================================================

/**
 * Watch for period changes to trigger data reload in machine
 * Note: Machine should automatically reload when period changes
 */
watch(periodId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    log('PAYROLL_REFACTOR', 'REFACTOR_TRACE', 'StepPayrollRefunds', 'PERIOD_CHANGED', {
      oldPeriodId: oldId,
      newPeriodId: newId,
      action: 'Machine will auto-reload refunds data'
    });
    // Machine handles reload via state transitions
  }
}, { immediate: false });
</script>

<style scoped>
/* Step Header */
.step-header h2 {
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

.step-panel {
  padding: 1.5rem;
}

/* Helper Banner */
.helper-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--blue-50);
  border-left: 4px solid var(--blue-500);
  border-radius: 6px;
  margin-bottom: 1rem;
}

.helper-banner i {
  color: var(--blue-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.helper-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--blue-700);
}

.helper-content ul {
  margin: 0;
  padding-left: 1.25rem;
}

.helper-content li {
  margin-bottom: 0.25rem;
  color: var(--blue-900);
  font-size: 0.9rem;
}

/* Warning Banner */
.warning-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--orange-50);
  border-left: 4px solid var(--orange-500);
  border-radius: 6px;
  margin-bottom: 1rem;
}

.warning-banner i {
  color: var(--orange-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.warning-content strong {
  display: block;
  margin-bottom: 0.25rem;
  color: var(--orange-700);
}

.warning-content p {
  margin: 0;
  color: var(--orange-900);
  font-size: 0.9rem;
  line-height: 1.5;
}

/* Important Notes */
.important-notes {
  padding: 1.25rem;
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  margin-bottom: 1rem;
}

.notes-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--surface-border);
}

.notes-header i {
  font-size: 1.25rem;
  color: var(--primary-color);
}

.notes-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.notes-list {
  margin: 0;
  padding-left: 0;
  list-style: none;
}

.notes-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  color: var(--text-color);
  line-height: 1.5;
}

.notes-list li i {
  color: var(--primary-color);
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.step-description {
  color: var(--text-color-secondary);
  margin-bottom: 1rem;
}

.info-message {
  margin-bottom: 1.5rem;
}

.reconfirm-warning-message {
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
}

.warning-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.warning-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--yellow-900);
}

.warning-text {
  margin: 0;
  color: var(--yellow-800);
  line-height: 1.5;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem;
}

.refunds-container {
  margin-top: 1.5rem;
}

/* Status Card */
.status-card {
  background: var(--surface-card);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  margin-bottom: 1.5rem;
}

.card-body {
  padding: 1.5rem;
}

/* Summary Grid */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.summary-item .item-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  font-weight: 500;
}

.summary-item .item-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.summary-item .item-value.pending-count {
  color: var(--yellow-600);
}

.summary-item .item-value.accepted-count {
  color: var(--green-600);
}

.summary-item.highlight .item-value {
  color: var(--primary-600);
  font-size: 1.5rem;
}

/* Status Indicator */
.status-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.status-indicator.empty {
  background-color: var(--surface-400);
}

.status-indicator.pending {
  background-color: var(--yellow-500);
  animation: pulse 1.5s infinite;
}

.status-indicator.completed {
  background-color: var(--green-500);
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Info Messages Inline */
.info-message-inline {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
}

.info-message-inline i {
  margin-top: 2px;
  flex-shrink: 0;
}

.info-message-inline.pending {
  background: var(--surface-50);
  color: var(--text-color-secondary);
}

.info-message-inline.pending i {
  color: var(--surface-500);
}

.info-message-inline.in-progress {
  background: var(--yellow-50);
  color: var(--yellow-800);
}

.info-message-inline.in-progress i {
  color: var(--yellow-500);
}

.info-message-inline.completed {
  background: var(--green-50);
  color: var(--green-800);
}

.info-message-inline.completed i {
  color: var(--green-500);
}

/* Pending Refunds Section */
.pending-refunds-section {
  margin-top: 1.5rem;
}

.pending-refunds-section h4 {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  color: var(--text-color);
}

.refunds-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.refund-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem;
  background: var(--surface-50);
  border: 1px solid var(--surface-200);
  border-left: 4px solid var(--yellow-500);
  border-radius: 6px;
  transition: all 0.2s;
}

.refund-item:hover {
  background: var(--surface-100);
  border-color: var(--surface-300);
}

.refund-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.refund-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.refund-title {
  font-weight: 600;
  font-size: 1rem;
  color: var(--text-color);
}

.refund-details {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.refund-details > div {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.refund-amount span {
  font-weight: 600;
  color: var(--primary-600);
}

.refund-description {
  font-size: 0.875rem;
  color: var(--text-color);
  line-height: 1.4;
  padding: 0.5rem;
  background: var(--surface-100);
  border-radius: 4px;
}

.refund-receipt {
  display: flex;
  align-items: center;
}

.receipt-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--primary-color);
  text-decoration: none;
}

.receipt-link:hover {
  text-decoration: underline;
}

.refund-actions {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-left: 1rem;
}

.w-full {
  width: 100%;
}

.mt-3 {
  margin-top: 0.75rem;
}

/* Responsive */
@media (max-width: 768px) {
  .summary-grid {
    grid-template-columns: 1fr 1fr;
  }

  .refund-item {
    flex-direction: column;
  }

  .refund-actions {
    margin-left: 0;
    margin-top: 1rem;
    width: 100%;
    justify-content: flex-end;
  }

  .refund-details {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
